import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_TIMEOUT_MS, API_URL as API } from '../lib/utils';
import { clearPrivateCaches } from '../lib/performance';

const AuthContext = createContext(null);
const SESSION_API = '/api-proxy/auth';
const SESSION_OPTIONS = { withCredentials: true, timeout: API_TIMEOUT_MS, headers: { 'X-Evo-Session': '1' } };
const LOGOUT_PENDING = 'evo_logout_pending';


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Access tokens are tab-local; only the protected cookie can restore a session.
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(null); // 'jwt' or 'google'
  const authGeneration = useRef(0);
  const logoutRequest = useRef(Promise.resolve());

  const publishSession = ({ token: nextToken, user: nextUser }) => {
    if (!nextToken || !nextUser) throw new Error('Respuesta de sesión inválida');
    clearPrivateCaches();
    sessionStorage.setItem('token', nextToken);
    sessionStorage.setItem('user', JSON.stringify(nextUser));
    axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    setToken(nextToken);
    setUser(nextUser);
    setAuthMethod('jwt');
  };

  useEffect(() => {
    let claims;
    try { claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return; }
    if (!claims.sid) return;
    let active = true;
    let renewing = false;
    const renew = async () => {
      if (renewing || !active) return;
      renewing = true;
      const generation = authGeneration.current;
      try {
        const response = await axios.post(`${SESSION_API}/restore`, {}, SESSION_OPTIONS);
        if (response.data.user.id !== claims.user_id) {
          throw Object.assign(new Error('La cuenta del dispositivo ha cambiado'), { response: { status: 401 } });
        }
        if (active && generation === authGeneration.current) publishSession(response.data);
      } catch (error) {
        if (active && generation === authGeneration.current
          && ([401, 403].includes(error.response?.status) || claims.exp * 1000 <= Date.now())) {
          clearPrivateCaches();
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
      } finally { renewing = false; }
    };
    const interval = setInterval(renew, 15 * 60 * 1000);
    const onFocus = () => { if (claims.exp * 1000 - Date.now() < 10 * 60 * 1000) renew(); };
    window.addEventListener('focus', onFocus);
    return () => { active = false; clearInterval(interval); window.removeEventListener('focus', onFocus); };
    // Renewal is tied to the current access token; identity comes from the server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Clear any legacy localStorage token to enforce session-only policy
    localStorage.removeItem('token');
    clearPrivateCaches();
    checkAuth();
    const generation = authGeneration;
    return () => { ++generation.current; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const generation = authGeneration.current;
    if (localStorage.getItem(LOGOUT_PENDING)) {
      try {
        await axios.post(`${SESSION_API}/logout`, {}, SESSION_OPTIONS);
        localStorage.removeItem(LOGOUT_PENDING);
      } catch { /* Keep retrying revocation on the next visit; never auto-restore. */ }
      setLoading(false);
      return;
    }
    // First check for JWT token
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const response = await axios.get(`${API}/auth/me`, { timeout: API_TIMEOUT_MS });
        if (generation !== authGeneration.current || sessionStorage.getItem('token') !== token) return;
        setUser(response.data);
        sessionStorage.setItem('user', JSON.stringify(response.data));
        setAuthMethod('jwt');
        setLoading(false);
        return;
      } catch (error) {
        if (generation !== authGeneration.current || sessionStorage.getItem('token') !== token) return;
        clearPrivateCaches();
        if (error.response?.status === 401 || error.response?.status === 403) {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      }
    }
    
    try {
      const response = await axios.post(`${SESSION_API}/restore`, {}, SESSION_OPTIONS);
      if (generation !== authGeneration.current) return;
      publishSession(response.data);
    } catch {
      // Missing, expired or unavailable remembered sessions fall back to the login form.
    }
    setLoading(false);
  };

  const login = async (username, password, { beforeSession, rememberSession = false } = {}) => {
    const generation = ++authGeneration.current;
    await logoutRequest.current;
    const response = await axios.post(`${API}/auth/login`, { username, password }, { timeout: API_TIMEOUT_MS });
    const { token: newToken, user: userData } = response.data;
    // Finish portal validation and browser credential storage before routing away.
    await beforeSession?.(userData);
    if (generation !== authGeneration.current) throw new Error('Acceso cancelado');
    let session = { token: newToken, user: userData };
    try {
      // Always clear the previous device session before switching accounts or opting out.
      await axios.post(`${SESSION_API}/logout`, {}, SESSION_OPTIONS);
      if (generation !== authGeneration.current) throw new Error('Acceso cancelado');
      if (rememberSession) {
        await axios.post(`${SESSION_API}/remember`, {}, {
          ...SESSION_OPTIONS, headers: { ...SESSION_OPTIONS.headers, Authorization: `Bearer ${newToken}` },
        });
        // Confirm that the browser actually accepted the HttpOnly cookie.
        const restored = await axios.post(`${SESSION_API}/restore`, {}, SESSION_OPTIONS);
        if (restored.data.user.id !== userData.id) throw new Error('Sesión incorrecta');
        session = restored.data;
      }
      localStorage.removeItem(LOGOUT_PENDING);
    } catch {
      localStorage.setItem(LOGOUT_PENDING, '1');
      const error = new Error('No se pudo preparar la sesión. Comprueba la conexión y permite las cookies de este sitio.');
      error.sessionSetup = true;
      throw error;
    }
    if (generation !== authGeneration.current) {
      await axios.post(`${SESSION_API}/logout`, {}, SESSION_OPTIONS);
      throw new Error('Acceso cancelado');
    }
    publishSession(session);
    return userData;
  };

  const register = async (username, password) => {
    const response = await axios.post(`${API}/auth/register`, { username, password }, { timeout: API_TIMEOUT_MS });
    const { token: newToken, user: userData, pending_approval } = response.data;
    
    if (!pending_approval) {
      clearPrivateCaches();
      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      setAuthMethod('jwt');
    }
    
    return { user: userData, pending_approval };
  };

  const loginWithGoogle = () => {
    // Google Auth via Emergent is disabled.
    // Implement standard Google OAuth here if needed.
    console.warn("Google Auth is disabled in this version.");
  };

  const setGoogleUser = (userData) => {
    clearPrivateCaches();
    setUser(userData);
    setAuthMethod('google');
  };

  const logout = async () => {
    ++authGeneration.current;
    localStorage.setItem(LOGOUT_PENDING, '1');
    clearPrivateCaches();
    setLoading(false);
    // Clear JWT token
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    
    setUser(null);
    setAuthMethod(null);
    logoutRequest.current = axios.post(`${SESSION_API}/logout`, {}, SESSION_OPTIONS).then(() => {
      localStorage.removeItem(LOGOUT_PENDING);
    }).catch(() => {
      // A failed network revocation must not silently sign the user back in.
    });
    await logoutRequest.current;
  };

  const isAuthenticated = !!user || !!token;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      loginWithGoogle,
      setGoogleUser,
      logout,
      isAuthenticated,
      authMethod,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
